import { useContext } from "react"
import UserContext from "../../contexts/UserContext/context"
import { useToast } from "../../hooks/useToast"
import { changePassword, editUser, updateUser } from "../../services/calls/users"
import { useTranslation } from "react-i18next"
import { setUser } from "../../contexts/UserContext/actions"
import LoadingSpinner from "../../components/LoadingSpinner"

type ErrorType = Record<string, boolean>

export const useProfileSettings = () => {
  const { user, dispatch } = useContext(UserContext)

  const { addToast } = useToast()
  const { t } = useTranslation()

  const lat = user?.preferences?.MAP_LATITUDE_ON_LOAD
  const lon = user?.preferences?.MAP_LONGITUDE_ON_LOAD

  const formatUser = {
    ...user,
    landing_page: user?.preferences?.DEFAULT_LANDING_SITE,
    map_fullscreen: user?.preferences?.MAP_FULLSCREEN_ON_LOAD,
    stop_limit: user?.preferences?.CUSTOM_STOP_LIMIT,
    hide_stop_history: user?.preferences?.HIDE_STOPS_HISTORY,
    map_freeze: user?.preferences?.DISABLE_ZOOM_AND_DRAG,
    zoom: user?.preferences?.MAP_ZOOM_LEVEL_ON_LOAD,
    center: [lon, lat]
  }

  const formatChanges = (change: any) => {
    const data = { ...change }
    const preferences: any = {}
    if (change.zoom) preferences.map_zoom_level_on_load = parseInt(change.zoom)
    if (change.center) {
      preferences.map_latitude_on_load = change.center[1]
      preferences.map_longitude_on_load = change.center[0]
    }
    if (change.map_fullscreen) preferences.map_fullscreen_on_load = change.map_fullscreen
    if (change.stop_limit) preferences.custom_stop_limit = change.stop_limit
    if (change.landing_page) preferences.default_landing_site = change.landing_page
    if (change.hide_stop_history) preferences.hide_stops_history = change.hide_stop_history
    if (change.map_freeze) preferences.disable_zoom_and_drag = change.map_freeze

    if (Object.keys(preferences).length) data.preferences = preferences
    return data
  }

  const saveChanges = async (change: any, error: ErrorType, setError: (val: ErrorType) => void, setIsLoading: (val: boolean) => void) => {
    setIsLoading(true)
    let passwordValid = true
    if (change.new_password) {
      if (change.new_password !== change.confirm_password) {
        setError({ ...error, confirm_password: { type: 'error', message: t('Passwords do not match') } })
        passwordValid = false
      } else {
        setError({})
        try {
          await changePassword(user.id, {
            old_password: change.old_password, new_password: change.new_password
          })
          addToast({
            label: t('New password'),
            message: t('Your password has been changed'),
            status: 'success'
          })
        } catch (err) {
          const { error: message } = err.response?.data
          if (message) setError({ ...error, old_password: { type: 'error', message }})
          setIsLoading(false)
        }
      }
    }

    if (passwordValid) {
      const data = formatChanges(change)
      try {
        await editUser(user.id, data)
        addToast({
          label: t('Profile information'),
          message: t('Your profile information was saved successfully'),
          status: 'success'
        })
        const newUser = await updateUser()
        newUser.name = `${newUser.first_name} ${newUser.last_name}`
        dispatch(setUser(newUser))
      } catch (err) {
        const { error: message } = err.response?.data
        if (message) {
          addToast({
            label: t('Error'),
            message,
            status: 'error'
          })
        } else {
          console.log('error', err)
        }
        setIsLoading(false)
      }
    }
    setIsLoading(false)
  }

  const buttonLabel = (isLoading: boolean) => {
    if (isLoading) {
      return (
        <>
          {`${t('Loading')}...`}
          <LoadingSpinner />
        </>
      )
    }

    return t('Save changes')
  }

  return {
    formatUser,
    saveChanges,
    buttonLabel
  }
}
